/* Track previews for product pages — both the standalone "Play sample"
   button (first track) and the per-track buttons in the tracklist
   accordion. Looks up a 30-second clip via Apple's public iTunes Search API
   (no key required, CORS-open) matched on "artist + track title" — these are
   Apple's own hosted previews, not a recording of the specific copy for
   sale, and only fetched lazily when a customer presses play. Each button
   carries (or inherits from a `[data-preview-artist]` ancestor) the artist
   name, so buttons outside the tracklist container work the same way. */
(function () {
  'use strict';

  if (!document.querySelector('[data-preview-btn]')) return;

  var strings = window.wileyStrings || {};
  var cache = {}; // track title -> previewUrl ('' = looked up, none found)
  var audio = new Audio();
  audio.preload = 'none';
  var activeBtn = null;

  var ICONS = {
    play: '<svg class="icon" viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M7 4.5v15l13-7.5-13-7.5Z"/></svg>',
    pause: '<svg class="icon" viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><rect x="6" y="4.5" width="4.5" height="15" rx="1"/><rect x="13.5" y="4.5" width="4.5" height="15" rx="1"/></svg>',
    loading: '<svg class="icon icon--spin" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" aria-hidden="true"><path d="M12 3a9 9 0 1 0 9 9" opacity=".85"/></svg>'
  };

  function setBtnState(btn, state) {
    // state: 'idle' | 'loading' | 'playing' | 'unavailable'
    btn.dataset.state = state;
    var iconName = state === 'loading' ? 'loading' : state === 'playing' ? 'pause' : 'play';
    var icon = btn.querySelector('.icon');
    if (icon) {
      var fresh = document.createElement('span');
      fresh.innerHTML = ICONS[iconName];
      var newIcon = fresh.firstElementChild;
      if (newIcon) icon.replaceWith(newIcon);
    }
    btn.setAttribute('aria-label', state === 'playing' ? (strings.previewPause || 'Pause preview') : (strings.previewPlay || 'Play preview'));
    var label = btn.querySelector('[data-preview-label]');
    if (label) {
      var idleText = btn.dataset.playLabel || strings.previewPlay || 'Play preview';
      label.textContent = state === 'loading' ? (strings.previewLoading || 'Loading…')
        : state === 'playing' ? (btn.dataset.pauseLabel || strings.previewPause || 'Pause preview')
        : state === 'unavailable' ? (strings.previewUnavailable || 'No preview found')
        : idleText;
    }
    if (state === 'unavailable') {
      btn.classList.add('is-unavailable');
      window.setTimeout(function () {
        btn.classList.remove('is-unavailable');
      }, 1600);
    } else {
      btn.classList.remove('is-unavailable');
    }
  }

  function normalize(s) {
    return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  // Plain-text search can return a same-titled track by an unrelated artist
  // (e.g. "Sweet Sue" matching a totally different act) — only trust a
  // result whose artist name actually corresponds to this record's artist,
  // rather than just taking the top hit.
  function pickMatch(results, artist) {
    if (!results || !results.length) return '';
    var wanted = normalize(artist);
    for (var i = 0; i < results.length; i++) {
      var got = normalize(results[i].artistName);
      if (wanted && got && (got.indexOf(wanted) !== -1 || wanted.indexOf(got) !== -1)) {
        return results[i].previewUrl || '';
      }
    }
    return '';
  }

  function resetActive() {
    if (activeBtn) setBtnState(activeBtn, 'idle');
    activeBtn = null;
  }

  audio.addEventListener('ended', resetActive);

  function playUrl(btn, url) {
    activeBtn = btn;
    audio.src = url;
    audio.play().then(function () {
      setBtnState(btn, 'playing');
    }).catch(function () {
      setBtnState(btn, 'unavailable');
      activeBtn = null;
    });
  }

  function btnArtist(btn) {
    var artistHost = btn.closest('[data-preview-artist]');
    return artistHost ? artistHost.getAttribute('data-preview-artist') : '';
  }

  // Looks up a track and hands the result to onDone(url) — url is '' when
  // no match was found. Shared by the click handler (which then plays it)
  // and the eager pre-check (which hides the button instead of playing).
  function lookup(btn, onDone) {
    var title = btn.getAttribute('data-track-title') || '';
    var cacheKey = title.toLowerCase();
    if (cache.hasOwnProperty(cacheKey)) {
      onDone(cache[cacheKey]);
      return;
    }
    var term = encodeURIComponent((btnArtist(btn) + ' ' + title).trim());
    var artist = btnArtist(btn);
    fetch('https://itunes.apple.com/search?term=' + term + '&entity=song&limit=5')
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var url = pickMatch(data && data.results, artist);
        cache[cacheKey] = url || '';
        onDone(url);
      })
      .catch(function () {
        cache[cacheKey] = '';
        onDone('');
      });
  }

  // Checks a button ahead of any click and hides it if there's no preview
  // for that track — so customers only ever see a play button that works,
  // rather than discovering it's broken after tapping it.
  function precheck(btn) {
    if (btn.dataset.checked) return;
    btn.dataset.checked = '1';
    setBtnState(btn, 'loading');
    lookup(btn, function (url) {
      if (btn.dataset.state !== 'loading') return; // user already clicked it
      if (url) {
        setBtnState(btn, 'idle');
      } else {
        btn.dataset.state = 'unavailable';
        btn.hidden = true;
      }
    });
  }

  document.querySelectorAll('.sample-btn[data-preview-btn]').forEach(precheck);

  document.querySelectorAll('[data-track-preview]').forEach(function (list) {
    var details = list.closest('details');
    if (!details) return;
    details.addEventListener('toggle', function () {
      if (!details.open) return;
      list.querySelectorAll('[data-preview-btn]').forEach(precheck);
    });
  });

  // Collection/search/homepage grid cards: same pre-check, but lazy — a
  // listing page can have dozens of cards, so checking every one on load
  // would mean dozens of API calls before a shopper's scrolled past the
  // first row. An IntersectionObserver checks each card only once it's
  // actually scrolled near the viewport, so the request volume tracks what
  // someone's actually looking at rather than the whole page at once.
  var CARD_SAMPLE_SELECTOR = '.record-card__sample[data-preview-btn]';
  var cardObserver = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      precheck(entry.target);
      cardObserver.unobserve(entry.target);
    });
  }, { rootMargin: '200px' }) : null;

  function watchCard(btn) {
    if (cardObserver) cardObserver.observe(btn);
    else precheck(btn);
  }

  document.querySelectorAll(CARD_SAMPLE_SELECTOR).forEach(watchCard);

  // Filter changes re-render the results grid via AJAX and "Load more"
  // appends a fresh batch — both inject card markup the observer above
  // never saw, so watch for it rather than only checking once at load.
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches(CARD_SAMPLE_SELECTOR)) watchCard(node);
          if (node.querySelectorAll) node.querySelectorAll(CARD_SAMPLE_SELECTOR).forEach(watchCard);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-preview-btn]');
    if (!btn) return;

    if (btn === activeBtn && btn.dataset.state === 'playing') {
      audio.pause();
      resetActive();
      return;
    }
    if (activeBtn && activeBtn !== btn) {
      audio.pause();
      resetActive();
    }

    setBtnState(btn, 'loading');
    lookup(btn, function (url) {
      if (btn.dataset.state !== 'loading') return; // user moved on
      if (url) {
        playUrl(btn, url);
      } else {
        setBtnState(btn, 'unavailable');
      }
    });
  });
})();
